/**
 * Plays the timeline's music in step with the editor's scene preview while
 * the timeline panel is mounted (i.e. while motion mode is on).
 */

import { useEffect } from 'react';
import { createTimelineAudioPreview, type TimelineAudioPreview } from '@/lib/video/timelineAudioPreview';
import { audioClipsToTracks, isClipProject } from '@/lib/video/timelineEdit';
import { useVideoStore, type VideoStore } from '@/stores/videoStore';

export function useTimelineAudioPreview(): void {
  useEffect(() => {
    const previews: TimelineAudioPreview[] = [];
    const sync = (s: VideoStore) => {
      const tracks = s.video && isClipProject(s.video)
        ? audioClipsToTracks(s.video)
        : s.video?.audio
          ? [s.video.audio]
          : [];
      while (previews.length < tracks.length) previews.push(createTimelineAudioPreview());
      const state = {
        playing: s.playback === 'playing',
        timelineMs: s.currentTimeMs,
        totalMs: s.video?.totalDurationMs ?? 0,
        muted: s.muted,
      };
      previews.forEach((preview, index) => {
        preview.setTrack(tracks[index] ?? null);
        preview.update(state);
      });
    };
    sync(useVideoStore.getState());
    const unsubscribe = useVideoStore.subscribe(sync);
    return () => {
      unsubscribe();
      previews.forEach((preview) => preview.destroy());
    };
  }, []);
}
